/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Typography } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import { selectServiceTierChangeRequestEmail } from '../../../store';
import appTheme from '../../common/theme/appTheme.json';

interface ServiceTierChangeRequestSuccessDialogProps {
  open: boolean;
  onDialogCancel: () => void;
}

const ServiceTierChangeRequestSuccessDialog = (props: ServiceTierChangeRequestSuccessDialogProps) => {
  const { open, onDialogCancel } = props;
  const serviceTierChangeRequestEmail = useSelector(selectServiceTierChangeRequestEmail, shallowEqual);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onDialogCancel();
        }}
        sx={{
          '.MuiDialog-paper': { width: '648px', height: '316px', borderRadius: '12px' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: appTheme.viasatFontFamilyRegular,
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '28px',
            letterSpacing: '0.2px',
            padding: '24px',
          }}
        >
          Request submitted
          <IconButton onClick={onDialogCancel} sx={{ padding: '0px' }}>
            <CloseRoundedIcon sx={{ color: appTheme.grayTextColor }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography sx={dialogBodyStyle}>Your service tier change request was submitted successfully.</Typography>
          <Typography sx={dialogBodyStyle}>
            A Viasat representative will review your request and notify you with the results of your request.
          </Typography>
          <Typography sx={dialogBodyStyle}>
            If you have questions regarding your request please{' '}
            <Link
              target="_blank"
              href={`mailto:${serviceTierChangeRequestEmail}`}
              underline="hover"
              component="a"
              sx={{
                color: appTheme.textLinkColor,
              }}
            >
              contact us
            </Link>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onDialogCancel();
            }}
            sx={{
              height: '40px',
              width: '98px',
              borderRadius: '1000px',
              backgroundColor: '#18ECFC',
              border: 'none',
              color: '#202E39',
              fontFamily: 'Source Sans Pro',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '24px',
              letterSpacing: 0,
              textAlign: 'center',
              '&:hover': {
                backgroundColor: '#18ECFC',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ServiceTierChangeRequestSuccessDialog;

const dialogBodyStyle = {
  fontFamily: 'Source Sans Pro',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#FFFFFF',
};
